import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers } from "../../services/crmServices/userService";
import { approveTransfer, updateTransfer } from "../../services/crmServices/transferService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { getFullName } from "../../services/common/util";
import moment from "moment";
import { useAuth } from "../../hooks/useAuth";

export function ApproveTransferModal({ open, onClose, onSuccess, transfer }) {
    const { user } = useAuth();
    const [targetDesk, setTargetDesk] = useState("");
    const [targetAgent, setTargetAgent] = useState("");
    const [notes, setNotes] = useState("");
    const [leadStatus, setLeadStatus] = useState("");
    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchDesks().then((res) => {
                if (res?.data?.length) {
                    setDesks(res.data);
                }
            });
            fetchLeadStatuses().then((res) => {
                if (res?.data?.length) {
                    setLeadStatuses(res.data);
                }
            });
            // Set initial status to current lead status
            if (transfer?.lead?.status) {
                setLeadStatus(transfer.lead.status);
            }
        }
    }, [open, transfer]);

    useEffect(() => {
        if (targetDesk) {
            searchUsers({ agentDesk: targetDesk }).then((res) => {
                if (res.data) {
                    setAgents(res.data);
                } else {
                    setAgents([]);
                }
            });
        } else {
            setAgents([]);
        }
        setTargetAgent("");
    }, [targetDesk]);

    const handleApprove = async () => {
        if (!targetDesk) {
            toast.error("Please select a target desk");
            return;
        }

        setLoading(true);

        // if(targetDesk)
        try {
            const approvalData = {
                _id: transfer._id,
                resolvedAt: moment().tz("America/New_York").toDate(),
                resolvedBy: user._id,
                status: "approved",
                targetDesk,
                targetAgent: targetAgent || null,
                approvalNotes: notes.trim() || null,
                leadStatus: leadStatus || null
            };

            const response = await updateTransfer(approvalData);

            if (response?.status === 200) {
                toast.success(
                    `Transfer approved successfully! Lead assigned to ${
                        targetAgent ? getFullName(agents.find((a) => a._id === targetAgent)) : "desk"
                    }.`
                );
                onSuccess();
            } else {
                toast.error(response?.data?.message || "Failed to approve transfer");
            }
        } catch (error) {
            console.error("Approval error:", error);
            toast.error("Failed to approve transfer");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTargetDesk("");
        setTargetAgent("");
        setNotes("");
        setLeadStatus("");
        onClose();
    };

    if (!transfer) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Approve Transfer</DialogTitle>
                    <DialogDescription>
                        Approve the transfer of {getFullName(transfer.lead)} and assign to a new desk/agent.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Transfer Details</h4>
                        <div className="text-sm space-y-1">
                            <p>
                                <strong>Lead:</strong> {getFullName(transfer.lead)}
                            </p>
                            <p>
                                <strong>Email:</strong> {transfer.lead.email}
                            </p>
                            <p>
                                <strong>From Agent:</strong> {getFullName(transfer.requestedBy)}
                            </p>
                            <p>
                                <strong>From Desk:</strong> {transfer.lead.desk?.name || "-"}
                            </p>
                            {transfer.reason && (
                                <p>
                                    <strong>Reason:</strong> {transfer.reason}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="targetDesk">Target Desk *</Label>
                            <Select
                                options={desks.map((desk) => ({
                                    label: desk.name,
                                    value: desk._id
                                }))}
                                value={targetDesk}
                                onChange={setTargetDesk}
                                placeholder="Select target desk"
                                searchable
                            />
                        </div>

                        <div>
                            <Label htmlFor="targetAgent">Target Agent (Optional)</Label>
                            <Select
                                options={agents.map((agent) => ({
                                    label: getFullName(agent),
                                    value: agent._id
                                }))}
                                value={targetAgent}
                                onChange={setTargetAgent}
                                placeholder={targetDesk ? "Select target agent (optional)" : "Select desk first"}
                                searchable
                                disabled={!targetDesk}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                If no agent is selected, the lead will be assigned to the desk for manual assignment.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="leadStatus">Lead Status</Label>
                            <Select
                                options={leadStatuses.map((status) => ({
                                    label: status.label || status.name || status.value,
                                    value: status.value
                                }))}
                                value={leadStatus}
                                onChange={setLeadStatus}
                                placeholder="Select status (optional)"
                                searchable
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Update the lead's status when approving the transfer.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="notes">Approval Notes (Optional)</Label>
                            <Textarea
                                id="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Add any notes about this transfer approval..."
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-blue-900 mb-2">Approval Impact</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Lead will be moved to the selected desk</li>
                            <li>• Lead will be assigned to the selected agent (if specified)</li>
                            <li>• Transfer will be removed from pending transfers</li>
                            <li>• Transferring agent will be notified of approval</li>
                            <li>• Lead history will be preserved</li>
                        </ul>
                    </div>

                    <div className="flex space-x-3">
                        <Button
                            onClick={handleApprove}
                            disabled={loading || !targetDesk}
                            className="flex-1 "
                        >
                            {loading ? "Approving..." : "Approve Transfer"}
                        </Button>
                        <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
